import { useId, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FiCheck, FiChevronDown } from 'react-icons/fi';
import { LANGUAGES } from '../locales';
import { AUTH_API_URL } from '../config';
import WorkspaceDialog from './WorkspaceDialog';

export default function InterfaceLanguageSelect({ persist = false, showHint = false, compact = false }) {
  const { t, i18n } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [open, setOpen] = useState(false);
  const labelId = useId();
  const current = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const selectedLanguage = LANGUAGES.find((language) => language.code === current) || LANGUAGES[0];
  const change = async (language) => {
    setOpen(false);
    setFeedback('');
    await i18n.changeLanguage(language);
    if (!persist) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Missing session');
      await axios.put(`${AUTH_API_URL}/api/users/profile/language`, { language }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFeedback('account.language_saved');
    } catch {
      // A failed account write must not undo the local interface choice.
      setFeedback('account.language_local_only');
    } finally { setSaving(false); }
  };
  return <div className={`ws-interface-language${compact ? ' ws-interface-language-compact' : ''}`}>
    <div className="ws-field"><span id={labelId} className={compact ? 'ws-sr-only' : undefined}>{t('account.interface_language')}</span>
      <button className="ws-language-trigger" type="button" onClick={() => setOpen(true)} disabled={saving}
        aria-labelledby={`${labelId} ${labelId}-value`} aria-haspopup="dialog" aria-expanded={open} title={selectedLanguage.label}>
        <img className="ws-language-flag" src={`${process.env.PUBLIC_URL || ''}${selectedLanguage.flagImage}`} alt="" width="28" height="21" />
        <span id={`${labelId}-value`} className={compact ? 'ws-sr-only' : 'ws-language-name'}>{selectedLanguage.label}</span>
        <FiChevronDown aria-hidden="true" />
      </button>
    </div>
    {open && <WorkspaceDialog title={t('account.interface_language')} onClose={() => setOpen(false)} className="ws-language-dialog">
      <ul className="ws-language-options">
        {LANGUAGES.map(({ code, label, flagImage }) => <li key={code}>
          <button type="button" aria-pressed={code === selectedLanguage.code} onClick={() => change(code)}>
            <img className="ws-language-flag" src={`${process.env.PUBLIC_URL || ''}${flagImage}`} alt="" width="28" height="21" />
            <span className="ws-language-name" dir="auto">{label}</span>
            {code === selectedLanguage.code && <FiCheck aria-hidden="true" />}
          </button>
        </li>)}
      </ul>
    </WorkspaceDialog>}
    {showHint && <p className="ws-field-hint">{t('account.interface_language_desc')}</p>}
    {feedback && <p className="ws-setting-feedback" role="status">{t(feedback)}</p>}
  </div>;
}
